import React, { useEffect, useState } from "react";
import SummaryApi from "../../../common";
import AWS from 'aws-sdk';

// Configure AWS S3
AWS.config.update({
  accessKeyId: process.env.REACT_APP_AWS_ACCESS_KEY_ID,
  secretAccessKey: process.env.REACT_APP_AWS_SECRET_ACCESS_KEY,
  region: process.env.REACT_APP_AWS_REGION,
});
const s3 = new AWS.S3();

export default function StaffPage() {
  const [staffList, setStaffList] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [editingStaff, setEditingStaff] = useState(null);
  const [photoFile, setPhotoFile] = useState(null);
  const [uploadingPhoto, setUploadingPhoto] = useState(false);

  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    alternatePhone: "",
    address: "",
    aadharNumber: "",
    joiningDate: "",
    isActive: true,
    designation: "",
    shortDescription: "",
    experience: "",
    skillSet: "",
    education: "",
    photo: "",
    displayOnDashboard: false,
    socialMedia: {
      instagram: "",
      facebook: "",
      linkedin: "",
      github: "",
    },
  });

  // ✅ Fetch all staff
  const fetchStaff = async () => {
    try {
      const res = await fetch(SummaryApi.staff.getAll.url);
      const data = await res.json();
      setStaffList(data);
    } catch (err) {
      console.error("Error fetching staff:", err);
    }
  };

  useEffect(() => {
    fetchStaff();
  }, []);

  // ✅ Handle input change
  const handleChange = (e) => {
    const { name, value } = e.target;

    if (["instagram", "facebook", "linkedin", "github"].includes(name)) {
      setForm((prev) => ({
        ...prev,
        socialMedia: { ...prev.socialMedia, [name]: value },
      }));
    } else {
      setForm({ ...form, [name]: value });
    }
  };

  // ✅ Handle checkbox toggles
  const handleCheckbox = (e) => {
    const { name, checked } = e.target;
    setForm({ ...form, [name]: checked });
  };

  // ✅ Handle photo file selection
  const handlePhotoChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Validate file type
      if (!file.type.startsWith('image/')) {
        alert('Please select an image file');
        return;
      }
      // Validate file size (max 5MB)
      if (file.size > 5 * 1024 * 1024) {
        alert('File size should be less than 5MB');
        return;
      }
      setPhotoFile(file);
    }
  };

  // ✅ Upload photo to S3
  const uploadPhotoToS3 = async (file) => {
    const params = {
      Bucket: process.env.REACT_APP_BUCKET_NAME,
      Key: `staff_photos/${Date.now()}_${file.name}`,
      Body: file,
      ContentType: file.type,
    };

    return new Promise((resolve, reject) => {
      s3.upload(params, (err, data) => {
        if (err) reject(err);
        else resolve(data.Location);
      });
    });
  };

  // ✅ Create or update staff
  const handleSubmit = async (e) => {
    e.preventDefault();
    setUploadingPhoto(true);
    
    try {
      let photoUrl = form.photo;

      // Upload photo if a new file is selected
      if (photoFile) {
        photoUrl = await uploadPhotoToS3(photoFile);
      }

      const payload = {
        ...form,
        photo: photoUrl,
        skillSet:
          typeof form.skillSet === "string"
            ? form.skillSet
                .split(",")
                .map((s) => s.trim())
                .filter((s) => s)
            : [],
      };

      const { method, url } = editingStaff
        ? SummaryApi.staff.update(editingStaff._id)
        : SummaryApi.staff.create;

      const res = await fetch(url, {
        method,
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });

      const data = await res.json();
      if (res.ok) {
        alert(editingStaff ? "Staff updated!" : "Staff created!");
        resetForm();
        setShowModal(false);
        fetchStaff();
      } else {
        alert(data.message || "Error occurred");
      }
    } catch (err) {
      console.error("Error submitting form:", err);
      alert("Error uploading photo or saving staff data");
    } finally {
      setUploadingPhoto(false);
    }
  };

  const resetForm = () => {
    setForm({
      name: "",
      email: "",
      phone: "",
      alternatePhone: "",
      address: "",
      aadharNumber: "",
      joiningDate: "",
      isActive: true,
      designation: "",
      shortDescription: "",
      experience: "",
      skillSet: "",
      education: "",
      photo: "",
      displayOnDashboard: false,
      socialMedia: {
        instagram: "",
        facebook: "",
        linkedin: "",
        github: "",
      },
    });
    setEditingStaff(null);
    setPhotoFile(null);
  };

  // ✅ Edit staff
  const handleEdit = (staff) => {
    setEditingStaff(staff);
    setForm({
      name: staff.name || "",
      email: staff.email || "",
      phone: staff.phone || "",
      alternatePhone: staff.alternatePhone || "",
      address: staff.address || "",
      aadharNumber: staff.aadharNumber || "",
      joiningDate: staff.joiningDate
        ? staff.joiningDate.split("T")[0]
        : "",
      isActive: staff.isActive ?? true,
      designation: staff.designation || "",
      shortDescription: staff.shortDescription || "",
      experience: staff.experience || "",
      skillSet: (staff.skillSet || []).join(", "),
      education: staff.education || "",
      photo: staff.photo || "",
      displayOnDashboard: staff.displayOnDashboard || false,
      socialMedia: {
        instagram: staff.socialMedia?.instagram || "",
        facebook: staff.socialMedia?.facebook || "",
        linkedin: staff.socialMedia?.linkedin || "",
        github: staff.socialMedia?.github || "",
      },
    });
    setPhotoFile(null);
    setShowModal(true);
  };

  // ✅ Delete staff
  const handleDelete = async (id) => {
    if (!window.confirm("Are you sure you want to delete this staff?")) return;
    try {
      const { method, url } = SummaryApi.staff.delete(id);
      const res = await fetch(url, { method });
      const data = await res.json();
      if (res.ok) {
        alert("Staff deleted successfully");
        fetchStaff();
      } else {
        alert(data.message || "Error deleting staff");
      }
    } catch (err) {
      console.error("Error deleting staff:", err);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      {/* Header */}
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-semibold text-gray-800">Staff Management</h1>
        <button
          onClick={() => {
            resetForm();
            setShowModal(true);
          }}
          className="bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700 transition"
        >
          + Create Staff
        </button>
      </div>

      {/* Table */}
      <div className="overflow-x-auto bg-white rounded-lg shadow">
        <table className="w-full border-collapse">
          <thead className="bg-gray-100 border-b">
            <tr>
              <th className="p-3 text-left text-sm font-semibold">Photo</th>
              <th className="p-3 text-left text-sm font-semibold">Name</th>
              <th className="p-3 text-left text-sm font-semibold">Email</th>
              <th className="p-3 text-left text-sm font-semibold">Designation</th>
              <th className="p-3 text-left text-sm font-semibold">Phone</th>
              <th className="p-3 text-left text-sm font-semibold">Experience</th>
              <th className="p-3 text-left text-sm font-semibold">Dashboard</th>
              <th className="p-3 text-left text-sm font-semibold text-center">Actions</th>
            </tr>
          </thead>
          <tbody>
            {staffList.length === 0 ? (
              <tr>
                <td colSpan="8" className="text-center p-4 text-gray-500">
                  No staff found
                </td>
              </tr>
            ) : (
              staffList.map((staff) => (
                <tr
                  key={staff._id}
                  className="border-b hover:bg-gray-50 transition"
                >
                  <td className="p-3">
                    {staff.photo ? (
                      <img 
                        src={staff.photo} 
                        alt={staff.name} 
                        className="w-10 h-10 rounded-full object-cover"
                      />
                    ) : (
                      <div className="w-10 h-10 rounded-full bg-gray-300 flex items-center justify-center text-gray-600 text-xs">
                        No Photo
                      </div>
                    )}
                  </td>
                  <td className="p-3">{staff.name}</td>
                  <td className="p-3">{staff.email}</td>
                  <td className="p-3">{staff.designation || "—"}</td>
                  <td className="p-3">{staff.phone || "—"}</td>
                  <td className="p-3">{staff.experience || "—"}</td>
                  <td className="p-3 text-center">
                    {staff.displayOnDashboard ? (
                      <span className="text-green-600 font-medium">Yes</span>
                    ) : (
                      <span className="text-gray-500">No</span>
                    )}
                  </td>
                  <td className="p-2 flex justify-center gap-2">
                    <button
                      onClick={() => handleEdit(staff)}
                      className="bg-yellow-500 text-white px-2 py-1 rounded-md"
                    >
                      Update
                    </button>
                    <button
                      onClick={() => handleDelete(staff._id)}
                      className="bg-red-500 text-white px-2 py-1 rounded-md"
                    >
                      Delete
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg w-full max-w-2xl p-6 relative shadow-lg max-h-[90vh] overflow-y-auto">
            <h2 className="text-lg font-semibold mb-4">
              {editingStaff ? "Update Staff" : "Create New Staff"}
            </h2>

            <form onSubmit={handleSubmit} className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <input name="name" placeholder="Full Name" value={form.name} onChange={handleChange} required className="border p-2 rounded" />
              <input name="email" placeholder="Email" value={form.email} onChange={handleChange} required className="border p-2 rounded" />
              <input name="designation" placeholder="Designation" value={form.designation} onChange={handleChange} className="border p-2 rounded" />
              <input name="phone" placeholder="Phone" value={form.phone} onChange={handleChange} className="border p-2 rounded" />
              <input name="alternatePhone" placeholder="Alternate Phone" value={form.alternatePhone} onChange={handleChange} className="border p-2 rounded" />
              <input name="aadharNumber" placeholder="Aadhar Number" value={form.aadharNumber} onChange={handleChange} className="border p-2 rounded" />
              <input name="experience" placeholder="Experience (e.g. 2 years)" value={form.experience} onChange={handleChange} className="border p-2 rounded" />
              <input name="education" placeholder="Education" value={form.education} onChange={handleChange} className="border p-2 rounded" />
              <input type="date" name="joiningDate" value={form.joiningDate} onChange={handleChange} className="border p-2 rounded" />

              {/* Photo Upload Section */}
              <div className="col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Staff Photo
                </label>
                <input 
                  type="file" 
                  accept="image/*" 
                  onChange={handlePhotoChange} 
                  className="border p-2 rounded w-full"
                />
                {photoFile && (
                  <p className="text-sm text-green-600 mt-1">
                    Selected: {photoFile.name}
                  </p>
                )}
                {form.photo && !photoFile && (
                  <div className="mt-2">
                    <p className="text-sm text-gray-600 mb-1">Current photo:</p>
                    <img 
                      src={form.photo} 
                      alt="Staff preview" 
                      className="w-20 h-20 rounded-full object-cover border"
                    />
                  </div>
                )}
              </div>

              <textarea name="shortDescription" placeholder="Short Description" value={form.shortDescription} onChange={handleChange} className="border p-2 rounded col-span-2" />
              <textarea name="address" placeholder="Address" value={form.address} onChange={handleChange} className="border p-2 rounded col-span-2" />
              <input name="skillSet" placeholder="Skill Set (comma-separated)" value={form.skillSet} onChange={handleChange} className="border p-2 rounded col-span-2" />

              <div className="flex flex-col gap-2 col-span-2">
                <h3 className="font-medium text-gray-700">Social Media Links</h3>
                <div className="grid grid-cols-2 gap-2">
                  <input name="instagram" placeholder="Instagram" value={form.socialMedia.instagram} onChange={handleChange} className="border p-2 rounded" />
                  <input name="facebook" placeholder="Facebook" value={form.socialMedia.facebook} onChange={handleChange} className="border p-2 rounded" />
                  <input name="linkedin" placeholder="LinkedIn" value={form.socialMedia.linkedin} onChange={handleChange} className="border p-2 rounded" />
                  <input name="github" placeholder="GitHub" value={form.socialMedia.github} onChange={handleChange} className="border p-2 rounded" />
                </div>
              </div>

              <div className="flex items-center col-span-2 gap-4 mt-2">
                <label className="flex items-center gap-2">
                  <input type="checkbox" name="isActive" checked={form.isActive} onChange={handleCheckbox} /> Active
                </label>
                <label className="flex items-center gap-2">
                  <input type="checkbox" name="displayOnDashboard" checked={form.displayOnDashboard} onChange={handleCheckbox} /> Display on Dashboard
                </label>
              </div>

              <div className="flex justify-end col-span-2 mt-4 gap-3">
                <button 
                  type="button" 
                  onClick={() => setShowModal(false)} 
                  className="border border-gray-300 px-4 py-2 rounded hover:bg-gray-100"
                  disabled={uploadingPhoto}
                >
                  Cancel
                </button>
                <button 
                  type="submit" 
                  className="bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700 disabled:bg-blue-400"
                  disabled={uploadingPhoto}
                >
                  {uploadingPhoto ? "Uploading..." : editingStaff ? "Update" : "Create"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}